/**
 * What analytics and error reports may carry, and what they never may — with
 * NO runtime dependencies, so the customer client can import it without
 * dragging zod along (the reason `./contact` is its own module too).
 *
 * The rule both sides hold: a URL here is often a credential. `/s/<token>` is
 * a customer's fitting room, `/v/<key>` a friend's view of it, `bki_…` a
 * reservation, and the Koalendar webhook's secret is a path segment. None of
 * them may reach Google Analytics or Sentry, and neither may a name, an email
 * or a phone number. Everything leaving for either goes through here first.
 */

/** The client's runtime settings, written into the served page by the API (`window.__FR_CONFIG__`). */
export interface ClientTelemetryConfig {
  /** GA4 measurement id (`G-…`). Absent: no analytics at all, not even the script. */
  readonly gaMeasurementId?: string
  /** Sentry DSN for the browser. Absent: no error reporting. */
  readonly sentryDsn?: string
  /** `prod` or `test`, so the two never mix in one report. */
  readonly environment?: string
}

/** A path segment long and random enough to be a key: tokens, share keys, ids. */
const KEYLIKE = /^[A-Za-z0-9_-]{20,}$/

/**
 * A path with every credential taken out, and no query or fragment: the
 * shape of the page, not whose it is.
 *
 *   /s/7NqF…            → /s/:token
 *   /v/abc…             → /v/:token
 *   /api/hooks/koalendar/velvet-hour/<secret> → /api/hooks/koalendar/:tenant/:secret
 *   /api/reserve/bki_…/confirm → /api/reserve/:ref/confirm
 */
export function scrubPath(input: string): string {
  const path = (input.split(/[?#]/)[0] ?? '') || '/'
  const parts = path.split('/')
  const out = parts.map((part, index) => {
    const before = parts[index - 1]
    const twoBefore = parts[index - 2]
    if (!part) return part
    if (before === 's' || before === 'v') return ':token'
    if (twoBefore === 'koalendar' && before) return ':secret'
    if (before === 'koalendar' && twoBefore === 'hooks') return ':tenant'
    if (/^bki_/.test(part)) return ':ref'
    if (before === 'booked') return ':id'
    if (KEYLIKE.test(part)) return ':token'
    return part
  })
  return out.join('/')
}

/** A URL as `origin + scrubPath`: no query string, no fragment, no credential. */
export function scrubUrl(input: string): string {
  try {
    const url = new URL(input)
    return `${url.origin}${scrubPath(url.pathname)}`
  } catch {
    return scrubPath(input)
  }
}

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g
const PHONE = /\+?\d[\d\s().-]{7,}\d/g
const URLISH = /https?:\/\/[^\s"'<>]+/g
const BARE_PATH = /\/(?:s|v)\/[A-Za-z0-9_-]{8,}/g
const REF = /\bbki_[A-Za-z0-9]+/g
const BEARER = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi

/** Free text (an error message, a breadcrumb) with emails, phones, URLs' credentials and tokens taken out. */
export function scrubText(input: string): string {
  return input
    .replace(URLISH, (url) => scrubUrl(url))
    .replace(BARE_PATH, (path) => scrubPath(path))
    .replace(BEARER, 'Bearer [redacted]')
    .replace(REF, 'bki_[redacted]')
    .replace(EMAIL, '[email]')
    .replace(PHONE, '[phone]')
}

/**
 * The page path analytics may count, or `null` for a page it must not: the
 * staff console is the boutique's own work, not a customer's visit, and
 * counting it would only blur the funnel.
 */
export function analyticsPath(pathname: string): string | null {
  const path = scrubPath(pathname)
  if (path === '/admin' || path.startsWith('/admin/') || path === '/staff' || path.startsWith('/staff/')) return null
  return path
}
